<template>
    <v-container>
        <!-- Agrupación por anio_convenio -->
        <v-expansion-panels flat v-model="expandedPanels">
            <v-expansion-panel v-for="(grupo, index) in groupedByAnioConvenio" :key="grupo.anioConvenio"
                style="border: 1px solid #f0f0f0; border-radius: 0px;">
                <v-expansion-panel-header hide-actions class="ma-0 pa-0">
                    <template v-slot:default="{ open }">
                        <v-row align="center" class="ma-0">
                            <!-- Título del año/convenio -->
                            <v-col cols="8" md="8" class="d-flex align-center">
                                <v-icon :class="{ 'rotate-icon': open }" class="mr-2">
                                    mdi-chevron-down
                                </v-icon>
                                <div class="font-weight-regular text-body-1 mr-2">
                                    AÑO
                                </div>
                                <div class="font-weight-bold primary--text text-body-1">
                                    {{ getTituloGrupo(grupo) }}
                                </div>
                            </v-col>

                            <!-- Total del grupo -->
                            <v-col cols="4" md="4" class="d-flex justify-end align-center">
                                <div class="font-weight-bold"
                                    :class="$vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-1'">
                                    {{ labelTotal }}
                                    <v-chip :color="colorChip" dark label class="ml-2">
                                        {{ formatMoney(grupo.totalGrupo) }}
                                    </v-chip>
                                </div>
                            </v-col>
                        </v-row>
                    </template>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <!-- Tabla para desktop -->

                    <v-data-table v-if="!$vuetify.breakpoint.xsOnly && !$device.isMobile" :headers="headers"
                        :items="grupo.items" hide-default-footer :items-per-page="-1" disable-sort
                        :no-data-text="noDataText" class="elevation-0">

                        <!-- Slot para formatear mes_cuota -->
                        <template v-slot:item.mes_cuota="{ item }">
                            <slot name="item.mes_cuota" :item="item">
                                {{ getMonthName(item.mes_cuota) }}
                            </slot>
                        </template>

                        <!-- Slot para formatear el campo sumField (total por defecto) -->
                        <template v-slot:[`item.${sumField}`]="{ item }">
                            <slot :name="`item.${sumField}`" :item="item">
                                <div class="font-weight-bold text-body-2 primary--text">
                                    {{ formatMoney(item[sumField]) }}
                                </div>
                            </slot>
                        </template>

                        <!-- Slots dinámicos para columnas personalizadas -->
                        <template v-for="slot in customSlots" v-slot:[`item.${slot.name}`]="{ item }">
                            <slot :name="`item.${slot.name}`" :item="item">
                                {{ item[slot.name] }}
                            </slot>
                        </template>

                        <!-- Slot para acciones -->
                        <template v-if="showActions" v-slot:item.actions="{ item }">
                            <slot name="item.actions" :item="item">
                                <v-btn icon color="primary" @click="handleAction(item)">
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </slot>
                        </template>
                    </v-data-table>

                    <!-- Vista para móvil -->
                    <div v-else>
                        <v-card v-for="item in grupo.items" :key="getItemKey(item)"
                            class="ma-3 pa-2 elevation-0 rounded-0">
                            <slot name="mobile-card" :item="item">
                                <!-- Vista móvil por defecto -->
                                <v-row align="center">
                                    <!-- Tipo de aporte -->
                                    <!-- Campos adicionales opcionales -->
                                    <v-col v-for="header in effectiveMobileHeaders" :key="header.value" cols="7"
                                        v-show="header.value !== groupTitleField && header.value !== sumField && header.value !== 'mes_cuota'">
                                        <div class="d-inline font-weight-regular text-body-2 text-uppercase">
                                            <slot :name="`mobile.${header.value}`" :item="item">
                                                {{ formatValue(item[header.value], header) }} {{
                                                    getMonthName(item.mes_cuota) }}
                                            </slot>
                                        </div>
                                    </v-col>
                                    <!--                                     <v-col cols="7">
                                        <div class="font-weight-bold text-body-1">
                                            {{ item[groupTitleField] ? item[groupTitleField].toUpperCase() : 'N/A' }}
                                        </div>
                                    </v-col> -->

                                    <!-- Monto -->
                                    <v-col cols="5" class="d-flex justify-end">
                                        <div class="font-weight-bold text-body-2 primary--text">
                                            {{ formatMoney(item[sumField]) }}
                                        </div>
                                    </v-col>
                                </v-row>
                                <v-row v-if="grupo.items.length > 1 && item !== grupo.items[grupo.items.length - 1]">
                                    <v-col>
                                        <v-divider></v-divider>
                                    </v-col>
                                </v-row>
                                <v-row v-if="false">
                                    <!-- Mes/Cuota -->
                                    <v-col cols="12">
                                        Mes/Cuota:
                                        <div class="d-inline font-weight-bold">
                                            {{ item.mes_cuota || 'N/A' }}
                                        </div>
                                    </v-col>

                                    <!-- Acciones en móvil -->
                                    <v-col v-if="showActions" cols="12" class="mt-2">
                                        <slot name="mobile.actions" :item="item">
                                            <v-btn icon color="primary" @click="handleAction(item)">
                                                <v-icon>mdi-dots-vertical</v-icon>
                                            </v-btn>
                                        </slot>
                                    </v-col>
                                </v-row>
                            </slot>
                        </v-card>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <!-- Total general (opcional) -->
        <v-card v-if="showTotalGeneral" class="mt-4 pa-4 rounded-0" outlined>
            <v-row align="center">
                <v-col cols="12" md="8">
                    <div class="font-weight-bold text-h6 primary--text">
                        TOTAL GENERAL
                    </div>
                </v-col>
                <v-col cols="12" md="4" class="d-flex justify-end">
                    <v-chip :color="colorChip" dark label large>
                        {{ formatMoney(totalGeneral) }}
                    </v-chip>
                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>

<script>
export default {
    name: 'TablaAgrupadaGenerica',

    props: {
        // Items a agrupar
        items: {
            type: Array,
            required: true,
            default: () => []
        },

        // Campo por el cual agrupar (ej: 'anio_convenio')
        groupBy: {
            type: String,
            default: 'anio_convenio'
        },

        // Campo a sumar para el total (ej: 'total')
        sumField: {
            type: String,
            default: 'total'
        },

        // Headers para la tabla
        headers: {
            type: Array,
            required: true,
            default: () => []
        },

        // Headers para vista móvil (si no se provee, usa todos los headers)
        mobileHeaders: {
            type: Array,
            default: null
        },

        // Color del chip
        colorChip: {
            type: String,
            default: 'red'
        },

        // Label para el total
        labelTotal: {
            type: String,
            default: ''
        },

        // Título del grupo (función o string)
        tituloGrupo: {
            type: [String, Function],
            default: null
        },

        // Campo usado como key único
        itemKey: {
            type: String,
            default: 'numero'
        },

        // Mostrar total general
        showTotalGeneral: {
            type: Boolean,
            default: false
        },

        // Mostrar columna de acciones
        showActions: {
            type: Boolean,
            default: false
        },

        // Texto cuando no hay datos
        noDataText: {
            type: String,
            default: 'No hay datos para mostrar'
        },

        // Forzar vista móvil
        isMobileView: {
            type: Boolean,
            default: false
        },

        // Slots personalizados para columnas
        customSlots: {
            type: Array,
            default: () => []
        },

        // Campo adicional para el título del grupo (ej: 'tap_desapo')
        groupTitleField: {
            type: String,
            default: null
        },

        // Formato de moneda
        currencyOptions: {
            type: Object,
            default: () => ({
                locale: 'es-AR',
                currency: 'ARS',
                style: 'currency'
            })
        }
    },

    data() {
        return {
            expandedPanels: 0, // Primer panel expandido por defecto
            monthNames: [
                '', 'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
                'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
            ]
        };
    },

    computed: {
        // Agrupar items por el campo especificado
        groupedByAnioConvenio() {
            const grouped = this.items.reduce((result, item) => {
                const groupValue = item[this.groupBy];

                if (!result[groupValue]) {
                    result[groupValue] = {
                        [this.groupBy]: groupValue,
                        items: [],
                        totalGrupo: 0,
                        groupData: {} // Para almacenar datos adicionales del primer item
                    };
                }

                result[groupValue].items.push(item);
                result[groupValue].totalGrupo += Number(item[this.sumField]) || 0;

                // Guardar datos adicionales del primer item (como tap_desapo)
                if (result[groupValue].items.length === 1 && this.groupTitleField) {
                    result[groupValue].groupData[this.groupTitleField] = item[this.groupTitleField];
                }

                return result;
            }, {});

            return Object.values(grouped).sort((a, b) => {
                // Ordenar por el campo de agrupación
                return b[this.groupBy] - a[this.groupBy];
            });
        },

        // Calcular total general
        totalGeneral() {
            return this.groupedByAnioConvenio.reduce((total, grupo) => {
                return total + grupo.totalGrupo;
            }, 0);
        },

        // Headers efectivos para móvil
        effectiveMobileHeaders() {
            if (this.mobileHeaders) {
                return this.mobileHeaders;
            }
            // Filtrar headers que no sean acciones
            return this.headers.filter(h => h.value !== 'actions');
        }
    },

    methods: {
        // Formatear dinero
        formatMoney(value) {
            return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
        },

        // Formatear valores según tipo de header
        formatValue(value, header) {
            if (value == null) return 'N/A';

            // Si el header tiene formato personalizado
            if (header.format === 'currency') {
                return this.formatMoney(value);
            }

            if (header.format === 'date') {
                return this.formatDate(value);
            }

            return value;
        },

        // Formatear fecha
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            // Puedes personalizar esto según tu formato
            return dateString;
        },
        getMonthName(monthNumber) {
            return this.monthNames[parseInt(monthNumber)] || monthNumber;
        },

        // Obtener título del grupo
        getTituloGrupo(grupo) {
            if (typeof this.tituloGrupo === 'function') {
                return this.tituloGrupo(grupo);
            }

            if (this.tituloGrupo) {
                return this.tituloGrupo;
            }

            // Título por defecto
            let titulo = `${grupo[this.groupBy]}`;

            // Si hay campo adicional para el título
            if (this.groupTitleField && grupo.groupData[this.groupTitleField]) {
                titulo = `${grupo.groupData[this.groupTitleField].toUpperCase()} - ${titulo}`;
            }

            return titulo;
        },

        // Obtener título móvil de un item
        getMobileTitulo(item) {
            if (this.groupTitleField) {
                return `${item[this.groupTitleField]} - ${item.mes_cuota}/${item[this.groupBy]}`;
            }
            return `${item.mes_cuota || 'N/A'}`;
        },

        // Obtener key único del item
        getItemKey(item) {
            return item[this.itemKey] || Math.random();
        },

        // Manejar acción sobre item
        handleAction(item) {
            this.$emit('item-action', item);
        }
    }
};
</script>

<style scoped>
.rotate-icon {
    transition: transform 0.3s ease;
    transform: rotate(-180deg);
}
</style>
